/**
 * HealingArcLanding — landing page for a specific healing arc.
 *
 * Responsibilities (in order):
 *   1. Tier check — must be Awakening (premium); redirect if not
 *   2. Load arc progress — create a new row if none exists yet
 *   3. Concurrent arc check — show blocker if a different arc is in_progress
 *   4. Pre-screen gate — two questions before session 1 (arc-specific)
 *   5. Pacing enforcement — 72h hard minimum between sessions
 *   6. Arc info display — description, Marta bio, session map, attribution
 *   7. Begin / Continue button
 *
 * Route: /healing-arc/:arcId (Awakening-protected)
 *
 * NOTE: The actual arc session pages (/healing-arc/:arcId/session/:sessionNum)
 * are Item #4 in the build sequence and are not yet implemented. The Begin button
 * links to that route so the routing is ready when the session pages ship.
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { db } from '../lib/supabase'
import { getArc, nextSessionUnlocksAt } from '../data/arcs'
import type { Arc, ArcId, ArcStatus } from '../data/arcs'
import { localizeArc } from '../data/arcs.pl'
import { useTranslation } from 'react-i18next'

// ── Icons ─────────────────────────────────────────────────────────────────────

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M12.5 15l-5-5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7l3.5 3.5 5.5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6.5 4V6.5l1.8 1.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}

// ── DB types ──────────────────────────────────────────────────────────────────

interface ArcProgressRow {
  id: string
  arc_id: ArcId
  status: ArcStatus
  current_session: number
  session_1_completed_at: string | null
  session_2_completed_at: string | null
  session_3_completed_at: string | null
}

// ── Pacing helpers ────────────────────────────────────────────────────────────

/** Module-level, so `t` is passed in rather than read from a hook. */
type TFn = (key: string, opts?: Record<string, unknown>) => string

function formatPacingWait(unlockAt: Date, t: TFn): string {
  const hoursLeft = Math.ceil((unlockAt.getTime() - Date.now()) / (1000 * 60 * 60))
  if (hoursLeft <= 24) return hoursLeft === 1 ? t('arcs.hour') : t('arcs.hours', { n: hoursLeft })
  const days = Math.ceil(hoursLeft / 24)
  return days === 1 ? t('arcs.day') : t('arcs.days', { n: days })
}

// Which session_N_completed_at timestamp gates the next session
function previousSessionCompletedAt(progress: ArcProgressRow): string | null {
  if (progress.current_session === 2) return progress.session_1_completed_at
  if (progress.current_session === 3) return progress.session_2_completed_at
  return null
}

// ── Pre-screen gate ───────────────────────────────────────────────────────────

interface PreScreenProps {
  arc: Arc
  onPass: () => void
  onRedirect: (message: string) => void
}

function PreScreenGate({ arc, onPass, onRedirect }: PreScreenProps) {
  const { t } = useTranslation()
  const [answers, setAnswers] = useState<Record<number, 'yes' | 'no' | null>>(
    Object.fromEntries(arc.preScreenQuestions.map((_, i) => [i, null]))
  )
  const [showWarning, setShowWarning] = useState<string | null>(null)

  const allAnswered = Object.values(answers).every(a => a !== null)

  function handleContinue() {
    for (let i = 0; i < arc.preScreenQuestions.length; i++) {
      const q = arc.preScreenQuestions[i]
      const answer = answers[i]
      if (answer === q.warningIf) {
        setShowWarning(q.warningMessage)
        return
      }
    }
    onPass()
  }

  if (showWarning) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-6">
        <div className="max-w-sm w-full">
          <div
            className="rounded-2xl px-5 py-5 mb-6"
            style={{ background: 'rgba(184,125,10,0.06)', border: '1.5px solid rgba(184,125,10,0.2)' }}
          >
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#6B5F7A' }}>
              {showWarning}
            </p>
            <p className="text-xs leading-relaxed" style={{ color: '#9B8FAA' }}>
              {t('arcs.returnWhenReady')}
            </p>
          </div>
          <button
            onClick={() => onRedirect(showWarning)}
            className="w-full py-3 rounded-xl text-sm font-medium transition-opacity hover:opacity-90 mb-3"
            style={{ background: '#3D1F62', color: '#fff' }}
          >
            {t('arcs.startStandardInstead')}
          </button>
          <button
            onClick={() => setShowWarning(null)}
            className="w-full py-3 text-sm transition-opacity hover:opacity-70"
            style={{ background: 'none', border: 'none', color: '#9B8FAA', cursor: 'pointer' }}
          >
            {t('arcs.goBackQuestions')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 px-6 pb-10 max-w-xl mx-auto w-full">
      <p className="text-sm font-medium mb-1" style={{ color: '#1C1128' }}>
        {t('arcs.twoQuestions')}
      </p>
      <p className="text-xs leading-relaxed mb-6" style={{ color: '#9B8FAA' }}>
        {t('arcs.answerHonestly')}
      </p>

      {arc.preScreenQuestions.map((q, i) => (
        <div key={i} className="mb-6">
          <p className="text-sm leading-relaxed mb-3" style={{ color: '#1C1128' }}>{q.text}</p>
          <div className="flex gap-3">
            {(['yes', 'no'] as const).map(opt => (
              <button
                key={opt}
                onClick={() => setAnswers(prev => ({ ...prev, [i]: opt }))}
                className="flex-1 py-3 rounded-xl text-sm font-medium transition-all"
                style={{
                  background: answers[i] === opt ? '#3D1F62' : 'rgba(255,255,255,0.8)',
                  color: answers[i] === opt ? '#fff' : '#6B5F7A',
                  border: answers[i] === opt ? '1.5px solid #3D1F62' : '1.5px solid #E4D5EE',
                }}
              >
                {opt === 'yes' ? t('arcs.yes') : t('arcs.no')}
              </button>
            ))}
          </div>
        </div>
      ))}

      <button
        onClick={handleContinue}
        disabled={!allAnswered}
        className="w-full py-4 rounded-xl text-sm font-medium transition-opacity"
        style={{
          background: allAnswered ? '#3D1F62' : '#E4D5EE',
          color: allAnswered ? '#fff' : '#9B8FAA',
          opacity: allAnswered ? 1 : 0.7,
          cursor: allAnswered ? 'pointer' : 'not-allowed',
        }}
      >
        {t('session.continue')}
      </button>

      {/* Therapist normalization (Sage requirement) */}
      <p className="text-xs text-center leading-relaxed mt-6" style={{ color: '#9B8FAA' }}>
        {t('arcs.notTherapyLong')}
        {t('arcs.lettersForYou')}
      </p>
    </div>
  )
}

// ── Session map ───────────────────────────────────────────────────────────────

interface SessionMapProps {
  arc: Arc
  completedCount: number
  currentSession: number
}

function SessionMap({ arc, completedCount, currentSession }: SessionMapProps) {
  return (
    <div className="flex flex-col gap-3 mb-6">
      {arc.sessions.map((s, i) => {
        const sessionNum = i + 1
        const isComplete = i < completedCount
        const isCurrent = sessionNum === currentSession && !isComplete
        const isLocked = sessionNum > currentSession

        return (
          <div
            key={i}
            className="flex items-start gap-4 px-4 py-4 rounded-xl"
            style={{
              background: isCurrent
                ? arc.colorSoft
                : 'rgba(255,255,255,0.5)',
              border: isCurrent
                ? `1.5px solid ${arc.color}40`
                : '1.5px solid #E4D5EE',
              opacity: isLocked ? 0.5 : 1,
            }}
          >
            {/* Session number / check */}
            <div
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5"
              style={{
                background: isComplete ? arc.color : isCurrent ? `${arc.color}20` : '#F0EBF4',
                color: isComplete ? '#fff' : arc.color,
              }}
            >
              {isComplete ? <CheckIcon /> : (
                <span className="text-xs font-medium">{sessionNum}</span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: '#9B8FAA' }}>
                {s.subtitle}
              </p>
              <p className="text-sm font-medium mb-1" style={{ color: '#1C1128' }}>
                {s.title}
              </p>
              <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A' }}>
                {s.description}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface HealingArcLandingProps {
  user: User
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'wrong_tier' }
  | { kind: 'concurrent_arc'; blockingArcName: string }
  | { kind: 'pre_screen'; arc: Arc }
  | { kind: 'pacing'; arc: Arc; progress: ArcProgressRow; unlockAt: Date }
  | { kind: 'ready'; arc: Arc; progress: ArcProgressRow | null }

export default function HealingArcLanding({ user }: HealingArcLandingProps) {
  const { t } = useTranslation()
  const { arcId } = useParams<{ arcId: string }>()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const [state, setState] = useState<PageState>({ kind: 'loading' })
  const lang = i18n.language

  useEffect(() => {
    let cancelled = false

    async function load() {
      // Localized here rather than at render: the pre-screen questions and
      // warning copy read off this object, and they must be in the user's
      // language before the gate is shown. localizeArc leaves warningIf and
      // the pacing hours alone, so the gate's behaviour is unchanged.
      const raw = getArc(arcId as ArcId)
      const arc = raw ? localizeArc(raw, lang) : undefined
      if (!arc) { setState({ kind: 'not_found' }); return }

      try {
        // Load tier + all arc progress rows in parallel
        const [profileResult, progressResult] = await Promise.all([
          Promise.resolve(
            db().from('profiles').select('subscription_tier').eq('id', user.id).single()
          ),
          Promise.resolve(
            db()
              .from('healing_arc_progress')
              .select('id, arc_id, status, current_session, session_1_completed_at, session_2_completed_at, session_3_completed_at')
              .eq('user_id', user.id)
          ),
        ])

        if (cancelled) return

        const tier = (profileResult.data as { subscription_tier: string } | null)?.subscription_tier ?? 'free'
        if (tier !== 'premium') { setState({ kind: 'wrong_tier' }); return }

        const allProgress = (progressResult.data ?? []) as ArcProgressRow[]

        // Concurrent arc check — is any OTHER arc in_progress?
        const activeOther = allProgress.find(p => p.arc_id !== arc.id && p.status === 'in_progress')
        if (activeOther) {
          // The blocking arc's name is shown to the user, so it needs localizing too.
          const blockingRaw = getArc(activeOther.arc_id)
          const blockingArc = blockingRaw ? localizeArc(blockingRaw, lang) : undefined
          setState({ kind: 'concurrent_arc', blockingArcName: blockingArc?.name ?? activeOther.arc_id })
          return
        }

        // Find progress for THIS arc
        const thisProgress = allProgress.find(p => p.arc_id === arc.id && p.status === 'in_progress')
          ?? allProgress.find(p => p.arc_id === arc.id && p.status === 'completed')
          ?? null

        // New user — show pre-screen gate
        if (!thisProgress) {
          setState({ kind: 'pre_screen', arc })
          return
        }

        // Completed arc — can restart (new row will be created); show ready state for now
        if (thisProgress.status === 'completed') {
          setState({ kind: 'ready', arc, progress: thisProgress })
          return
        }

        // In-progress arc — check pacing
        const prevCompleted = previousSessionCompletedAt(thisProgress)
        if (prevCompleted) {
          const unlockAt = nextSessionUnlocksAt(prevCompleted, arc.pacingHours)
          if (unlockAt > new Date()) {
            setState({ kind: 'pacing', arc, progress: thisProgress, unlockAt })
            return
          }
        }

        setState({ kind: 'ready', arc, progress: thisProgress })
      } catch {
        if (!cancelled) setState({ kind: 'not_found' })
      }
    }

    load()
    return () => { cancelled = true }
  }, [user.id, arcId])

  async function handlePreScreenPass(arc: Arc) {
    // Create the progress row — this is the moment the arc officially starts
    try {
      await Promise.resolve(
        db().from('healing_arc_progress').insert({
          user_id: user.id,
          arc_id: arc.id,
          status: 'in_progress',
          current_session: 1,
        })
      )
    } catch {
      // Row may already exist (e.g., rapid double-tap) — safe to ignore
    }
    navigate(`/healing-arc/${arc.id}/session/1`)
  }

  function handleBeginSession(arc: Arc, sessionNum: number) {
    navigate(`/healing-arc/${arc.id}/session/${sessionNum}`)
  }

  // ── Render states ──────────────────────────────────────────────────────────

  if (state.kind === 'loading') {
    return (
      <div className="min-h-svh flex items-center justify-center bg-[#FAF7F4]">
        <div className="w-5 h-5 rounded-full border-2 border-[#9B72C0] border-t-transparent animate-spin" />
      </div>
    )
  }

  if (state.kind === 'not_found') {
    return (
      <div className="min-h-svh flex items-center justify-center px-6 bg-[#FAF7F4]">
        <div className="text-center">
          <p className="text-sm mb-4" style={{ color: '#6B5F7A' }}>{t('arcs.arcNotFound')}</p>
          <button onClick={() => navigate('/healing-arcs')} style={{ color: '#9B72C0', fontSize: 14 }}>
            {t('arcs.backToArcs')}
          </button>
        </div>
      </div>
    )
  }

  if (state.kind === 'wrong_tier') {
    return (
      <div className="min-h-svh flex flex-col items-center justify-center px-6 bg-[#FAF7F4]">
        <div className="max-w-sm w-full text-center">
          <p className="text-sm mb-4" style={{ color: '#6B5F7A' }}>
            {t('arcs.partOfAwakeningTier')}
          </p>
          <button
            onClick={() => navigate('/profile')}
            className="w-full py-3 rounded-xl text-sm font-medium"
            style={{ background: '#3D1F62', color: '#fff' }}
          >
            {t('arcs.exploreAwakening')}
          </button>
        </div>
      </div>
    )
  }

  if (state.kind === 'concurrent_arc') {
    return (
      <div className="min-h-svh flex flex-col bg-[#FAF7F4]">
        <header className="flex items-center gap-3 px-6 py-5 max-w-xl mx-auto w-full">
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9B8FAA' }}
            aria-label={t('arcs.back')}
          >
            <BackIcon />
          </button>
        </header>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-sm w-full text-center">
            <p className="font-display text-xl mb-3" style={{ color: '#1C1128', fontStyle: 'italic' }}>
              {t('arcs.oneArcAtATime')}
            </p>
            <p className="text-sm leading-relaxed mb-6" style={{ color: '#6B5F7A' }}>
              You're currently working through{' '}
              <span style={{ fontStyle: 'italic' }}>{state.blockingArcName}</span>.
              {t('arcs.completeOrPause')}
            </p>
            <button
              onClick={() => navigate('/healing-arcs')}
              className="w-full py-3 rounded-xl text-sm font-medium"
              style={{ background: '#3D1F62', color: '#fff' }}
            >
              {t('arcs.returnToArcs')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (state.kind === 'pre_screen') {
    const { arc } = state
    return (
      <div
        className="min-h-svh flex flex-col"
        style={{
          background: `radial-gradient(ellipse at 60% 0%, ${arc.colorSoft} 0%, transparent 50%), #FAF7F4`,
        }}
      >
        <header className="flex items-center gap-3 px-6 py-5 max-w-xl mx-auto w-full">
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9B8FAA' }}
            aria-label={t('arcs.back')}
          >
            <BackIcon />
          </button>
          <div>
            <p className="text-xs font-medium uppercase tracking-widest" style={{ color: arc.color }}>
              {t('arcs.healingArc')}
            </p>
            <h1 className="font-display text-lg font-normal" style={{ color: '#1C1128', fontStyle: 'italic' }}>
              {arc.name}
            </h1>
          </div>
        </header>

        <PreScreenGate
          arc={arc}
          onPass={() => handlePreScreenPass(arc)}
          onRedirect={() => navigate('/')}
        />
      </div>
    )
  }

  if (state.kind === 'pacing') {
    const { arc, unlockAt } = state
    const waitLabel = formatPacingWait(unlockAt, t)
    return (
      <div
        className="min-h-svh flex flex-col"
        style={{
          background: `radial-gradient(ellipse at 60% 0%, ${arc.colorSoft} 0%, transparent 50%), #FAF7F4`,
        }}
      >
        <header className="flex items-center gap-3 px-6 py-5 max-w-xl mx-auto w-full">
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9B8FAA' }}
            aria-label={t('arcs.back')}
          >
            <BackIcon />
          </button>
          <div>
            <p className="text-xs font-medium uppercase tracking-widest" style={{ color: arc.color }}>
              {t('arcs.healingArc')}
            </p>
            <h1 className="font-display text-lg font-normal" style={{ color: '#1C1128', fontStyle: 'italic' }}>
              {arc.name}
            </h1>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-sm w-full text-center">
            <div
              className="flex items-center justify-center gap-2 mb-4"
              style={{ color: arc.color }}
            >
              <ClockIcon />
              <span className="text-sm font-medium">{waitLabel} remaining</span>
            </div>
            <p className="font-display text-xl mb-3" style={{ color: '#1C1128', fontStyle: 'italic' }}>
              {t('arcs.asksForSpace')}
            </p>
            <p className="text-sm leading-relaxed mb-2" style={{ color: '#6B5F7A' }}>
              {arc.pacingSoftLabel}
            </p>
            <p className="text-xs" style={{ color: '#9B8FAA' }}>
              The next session opens in {waitLabel}.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ── Ready state (arc info + begin/continue button) ─────────────────────────
  const { arc, progress } = state
  const completedCount = progress
    ? [progress.session_1_completed_at, progress.session_2_completed_at, progress.session_3_completed_at]
        .filter(Boolean).length
    : 0
  const currentSession = progress?.status === 'completed'
    ? arc.sessionCount  // Show as complete, don't offer restart from here yet
    : (progress?.current_session ?? 1)
  const isCompleted = progress?.status === 'completed'
  const ctaLabel = isCompleted
    ? 'Completed'
    : completedCount === 0
      ? 'Begin Session 1'
      : `Continue — Session ${currentSession}`

  return (
    <div
      className="min-h-svh flex flex-col"
      style={{
        background: `radial-gradient(ellipse at 60% 0%, ${arc.colorSoft} 0%, transparent 50%), #FAF7F4`,
      }}
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-6 py-5 max-w-xl mx-auto w-full">
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9B8FAA' }}
          aria-label={t('arcs.back')}
        >
          <BackIcon />
        </button>
        <div>
          <p className="text-xs font-medium uppercase tracking-widest" style={{ color: arc.color }}>
            {t('arcs.healingArc')}
          </p>
          <h1 className="font-display text-lg font-normal" style={{ color: '#1C1128', fontStyle: 'italic' }}>
            {arc.name}
          </h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-6 pb-10 max-w-xl mx-auto w-full page-enter">
        {/* Description */}
        <p className="text-sm leading-relaxed mb-6" style={{ color: '#6B5F7A' }}>
          {arc.description}
        </p>

        {/* Pacing note */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg mb-6"
          style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid #E4D5EE' }}
        >
          <ClockIcon />
          <p className="text-xs" style={{ color: '#6B5F7A' }}>
            3 sessions · {arc.pacingSoftLabel}
          </p>
        </div>

        {/* Session map */}
        <SessionMap
          arc={arc}
          completedCount={completedCount}
          currentSession={currentSession}
        />

        {/* Marta attribution block */}
        <div
          className="rounded-2xl px-5 py-4 mb-6"
          style={{ background: `${arc.colorSoft}`, border: `1px solid ${arc.color}20` }}
        >
          <p className="text-xs font-medium uppercase tracking-widest mb-2" style={{ color: arc.color }}>
            {t('arcs.aboutThisArc')}
          </p>
          <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A' }}>
            {arc.attributionBio}
          </p>
          <p className="text-xs mt-2" style={{ color: arc.color, fontStyle: 'italic' }}>
            {arc.attribution}
          </p>
        </div>

        {/* Therapist normalization (Sage requirement) */}
        <p className="text-xs leading-relaxed mb-6" style={{ color: '#9B8FAA' }}>
          {t('arcs.disclaimer1')}
          {t('arcs.disclaimer2')}
        </p>
      </main>

      {/* Sticky CTA */}
      <div
        className="px-6 pb-10 pt-4 max-w-xl mx-auto w-full"
        style={{ borderTop: '1px solid rgba(228,213,238,0.4)' }}
      >
        {isCompleted ? (
          <div className="text-center">
            <p className="text-sm" style={{ color: arc.color, fontStyle: 'italic' }}>
              {arc.completionLine}
            </p>
          </div>
        ) : (
          <button
            onClick={() => handleBeginSession(arc, currentSession)}
            className="w-full py-4 rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
            style={{ background: arc.color, color: '#fff' }}
          >
            {ctaLabel}
          </button>
        )}
      </div>
    </div>
  )
}
